import React from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ReactComponent as RadioIcon } from "../../Assets/iconsv2/generic/radioButtonIcon.svg";
import { ReactComponent as MultipleChoiceIcon } from "../../Assets/iconsv2/generic/checkBoxIcon.svg";
import { ReactComponent as DragIcon } from "../../Assets/iconsv2/generic/dragIcon.svg";
import { ReactComponent as CloseXIcon } from "../../Assets/iconsv2/generic/closeX.svg";
import { useDragHandle } from "../GenericComponents/DraggableList";
import {
  OPTION_MAX_LENGTH,
  MIN_OPTIONS,
  ANSWER_TYPE_SINGLE,
  ANSWER_TYPE_MULTIPLE,
} from "../../Constants/constantValues";

const PollOption = ({
  poll,
  option,
  optionIndex,
  isDragging = false,
  canEdit = true,
  hasError = false,
  errorMessage = null,
  onUpdateText,
  onDelete,
  optionInputRef = null,
}) => {
  const { t } = useTranslate();

  const DraggableOptionHandle = ({ children }) => {
    const dragHandleProps = useDragHandle();
    return (
      <OptionDragHandle 
        className="option-drag-handle"
        {...dragHandleProps}
      >
        {children}
      </OptionDragHandle>
    );
  };

  return (
    <OptionRow $isDragging={isDragging}>
      {canEdit && (
        <DraggableOptionHandle>
          <DragIcon width={14} height={14} />
        </DraggableOptionHandle>
      )}
      
      <OptionIcon $type={poll.answer_type}>
        {poll.answer_type === ANSWER_TYPE_SINGLE ? (
          <RadioIcon width={18} height={18} />
        ) : (
          <MultipleChoiceIcon width={18} height={18} />
        )}
      </OptionIcon>
      
      <OptionInputWrapper>
        <OptionInput
          ref={optionInputRef}
          value={option.option_text}
          onChange={(e) => onUpdateText(poll.id, option.id, e.target.value)}
          placeholder={t("conferences.enter_option")}
          maxLength={OPTION_MAX_LENGTH}
          disabled={!canEdit}
          readOnly={!canEdit}
          $hasError={hasError}
        />
        {hasError && errorMessage && (
          <FieldErrorSmall>{errorMessage}</FieldErrorSmall>
        )}
      </OptionInputWrapper>
      
      {canEdit && poll.options.length > MIN_OPTIONS && (
        <DeleteOptionButton
          type="button"
          onClick={() => onDelete(poll.id, option.id)}
        >
          <CloseXIcon width={14} height={14} />
        </DeleteOptionButton>
      )}
    </OptionRow>
  );
};

export default PollOption;

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px;
  border-radius: 6px;
  background: ${({ theme, $isDragging }) =>
    $isDragging ? theme.menu.selectedOpionBackground : "transparent"};
  border: 2px solid
    ${({ theme, $isDragging }) =>
      $isDragging ? theme.menu.selectedTextColor : "transparent"};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.menu.groupContentBackground};
  }
`;

const OptionDragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  padding: 6px;
  border-radius: 4px;
  margin-top: 6px;
  transition: all 0.2s ease;
  
  svg {
    opacity: 0.4;
    transition: opacity 0.2s ease;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
  
  &:hover {
    background: ${({ theme }) => theme.menu.background};
    
    svg {
      opacity: 0.7;
    }
  }
  
  &:active {
    cursor: grabbing;
    background: ${({ theme }) => theme.sidebar.borderColor}40;
    
    svg {
      opacity: 1;
    }
  }
`;

const OptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1;
  min-width: 22px;
  text-align: center;
  margin-top: 8px;
  
  svg {
    fill: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const OptionInputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 2px solid ${({ theme, $hasError }) =>
    $hasError ? theme.menu.deleteColor : theme.sidebar.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 400;
  opacity: ${({ readOnly }) => (readOnly ? 0.6 : 1)};
  cursor: ${({ readOnly }) => (readOnly ? "default" : "text")};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.menu.deleteColor : theme.menu.selectedTextColor};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    opacity: 0.6;
  }
`;

const FieldErrorSmall = styled.div`
  color: ${({ theme }) => theme.menu.deleteColor};
  font-size: 11px;
  font-weight: 500;
  margin-top: 4px;
  padding-left: 4px;
`;

const DeleteOptionButton = styled.button`
  padding: 8px;
  margin-top: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  svg {
    fill: ${({ theme }) => theme.menu.deleteColor};
    opacity: 0.8;
    transition: all 0.2s ease;
  }
  
  &:hover {
    background: ${({ theme }) => theme.menu.deleteColor}20;
    
    svg {
      opacity: 1;
      transform: scale(1.1);
    }
  }
  
  &:active {
    transform: scale(0.95);
  }
`;